import { cn } from "@/lib/utils";
import { ReactNode } from "react";

/**
 * Typography — Luminous Atrium
 * Instrument Sans (font-sans) for all UI; Instrument Serif italic only as opt-in accent.
 * Tight tracking, medium weight, generous leading.
 */

interface BaseProps {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

// ─── Display ─────────────────────────────────
export const DisplayXL = ({ children, className }: BaseProps) => (
  <h1
    className={cn(
      "font-sans font-medium text-5xl sm:text-6xl md:text-7xl tracking-[-0.03em] leading-[1.02] text-foreground",
      className
    )}
  >
    {children}
  </h1>
);

export const DisplayL = ({ children, className }: BaseProps) => (
  <h1
    className={cn(
      "font-sans font-medium text-4xl sm:text-5xl md:text-6xl tracking-[-0.03em] leading-[1.05] text-foreground",
      className
    )}
  >
    {children}
  </h1>
);

export const DisplayM = ({ children, className, style }: BaseProps) => (
  <h1
    className={cn(
      "font-sans font-medium text-3xl sm:text-4xl tracking-[-0.025em] leading-[1.08] text-foreground",
      className
    )}
    style={style}
  >
    {children}
  </h1>
);

// ─── Page Header ─────────────────────────────
interface PageHeaderProps extends BaseProps {
  variant?: "app" | "marketing" | "content";
}
export const PageHeader = ({ children, variant = "app", className }: PageHeaderProps) => {
  const variantStyles = {
    app: "text-2xl sm:text-3xl",
    marketing: "text-3xl sm:text-4xl md:text-5xl",
    content: "text-2xl sm:text-3xl md:text-4xl",
  };
  return (
    <h1
      className={cn(
        "font-sans font-medium tracking-[-0.025em] leading-[1.1] text-foreground",
        variantStyles[variant],
        className
      )}
    >
      {children}
    </h1>
  );
};

// ─── Section / Subsection ────────────────────
export const SectionHeader = ({ children, className }: BaseProps) => (
  <h2
    className={cn(
      "font-sans font-medium text-xl sm:text-2xl tracking-[-0.02em] leading-tight text-foreground",
      className
    )}
  >
    {children}
  </h2>
);

export const SubsectionHeader = ({ children, className }: BaseProps) => (
  <h3
    className={cn(
      "font-sans font-medium text-base sm:text-lg tracking-[-0.01em] text-foreground",
      className
    )}
  >
    {children}
  </h3>
);

// ─── Body ────────────────────────────────────
interface BodyTextProps extends BaseProps {
  variant?: "primary" | "secondary" | "large";
}
export const BodyText = ({ children, variant = "primary", className, style }: BodyTextProps) => {
  const classes = {
    primary: "text-sm md:text-[15px] leading-relaxed text-foreground/85",
    secondary: "text-sm leading-relaxed text-foreground/65",
    large: "text-base md:text-lg leading-relaxed text-foreground/80",
  };
  return (
    <p className={cn(classes[variant], className)} style={style}>
      {children}
    </p>
  );
};

export const Caption = ({ children, className }: BaseProps) => (
  <p className={cn("text-xs text-foreground/55", className)}>{children}</p>
);

export const LabelText = ({ children, className }: BaseProps) => (
  <span
    className={cn(
      "text-[11px] font-medium uppercase tracking-[0.18em] leading-none text-foreground/65",
      className
    )}
  >
    {children}
  </span>
);
